import React, { useState } from "react";
import CollapsibleCard from "../../../ForBrands/ui/CollapsibleCard";
import InsightList from "../../../ForBrands/ui/InsightList";
import { ArchetypeDot } from "../../../ForBrands/ui/LegendItem";
import RankChip, { MedianChip } from "../../../ForBrands/ui/RankChip";
import { TapPanel, openIndexIn, tapScopeOf, useTapPanel } from "../../../ForBrands/ui/TapPanel";
import RadarPlot from "../../../ForBrands/ui/charts/RadarPlot";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { EMPTY } from "../../../../utils/perfFormat";
import PlayerArchetypeModal from "./PlayerArchetypeModal";
import { IMPACT_NOTE } from "./athletePerformanceModel";
import { PerfTile, cx } from "./perfPieces";

// Player Archetype (Jayson's A1, spec B3 b): the five archetype metrics as
// a percentile radar, the archetype tag (its data colour with a 1px ring)
// and what it means, the athlete's Impact rating (value, what it is in one
// line, Top N% and the division median, the rank when it is a real place;
// the pop-up says what it is with its scale), and the Archetype read (the archetype's and the
// impact's insights). Each radar point opens its detail: the metric, the
// athlete's value and rank, the division median and the stats behind it,
// in a hover card on hover or keyboard focus (desktop), or in the panel
// under the radar after a tap on the point or on its row (phones). The (i)
// opens what the metrics mean (PlayerArchetypeModal). `view` is
// athletePerformanceModel.archetypeView(); while the peer group is being
// built the same card shows its shell (`shell`, notes 10/21 D4 c).

// One metric's detail, in the hover card and the phone panel alike.
export const MetricDetail = ({ metric }) => (
  <span className="flex flex-col gap-2">
    <span className="bm-label-m text-bm-ink">{`${metric.code} · ${metric.title}`}</span>
    <span className="flex items-baseline gap-2">
      <span className="bm-num-ml tabular-nums text-bm-ink">{metric.raw}</span>
      <span className={cx("bm-label-l", metric.hot ? "text-bm-orange" : "text-bm-muted")}>{metric.top}</span>
    </span>
    {metric.median !== EMPTY && <span className="bm-body-s text-bm-muted">{`Division median ${metric.median}`}</span>}
    {metric.drivers.length > 0 && (
      <span className="flex flex-col gap-1 border-t border-bm-border pt-2">
        {metric.drivers.map((d) => (
          <span key={d.key} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-2 bm-body-s">
            <span className="min-w-0 break-words text-bm-muted">{d.label}</span>
            <span className="font-semibold tabular-nums text-bm-ink">{d.display}</span>
            <span className="min-w-[52px] text-right text-bm-muted">{d.top}</span>
          </span>
        ))}
      </span>
    )}
  </span>
);

const ImpactStat = ({ impact, approximate }) => (
  <div className="flex w-full flex-wrap items-center justify-between gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3">
    <span className="flex min-w-0 flex-col gap-[2px]">
      <span className="bm-overline">Impact rating</span>
      <span className="bm-num-m tabular-nums text-bm-ink">{impact.value}</span>
      <span className="break-words bm-body-xs text-bm-muted">{IMPACT_NOTE}</span>
    </span>
    <span className="flex flex-col items-end gap-1">
      <span className="flex flex-wrap items-center justify-end gap-[6px]">
        <RankChip percentile={impact.percentile} approximate={approximate} />
        <MedianChip value={impact.median} decimals={2} />
      </span>
      {impact.rank && <span className="bm-body-s text-bm-muted">{impact.rank}</span>}
    </span>
  </div>
);

// Phones: a row per metric under the radar, each opening the same panel as
// its point.
const MetricRows = ({ metrics, tap }) => (
  // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
  <ul role="list" aria-label="Archetype metrics" className="flex w-full flex-col">
    {metrics.map((m, i) => (
      <li key={m.key} className={cx(i > 0 && "border-t border-bm-mutedbg")}>
        <button
          {...tap.triggerProps(i)}
          className={cx(
            "flex w-full items-center gap-3 rounded-[8px] px-1 py-[10px] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-bm-ink",
            tap.isOpen(i) ? "bg-bm-mutedbg" : "hover:bg-bm-subtle",
          )}
        >
          <span className="w-10 shrink-0 bm-label-l text-bm-orange">{m.code}</span>
          <span className="min-w-0 flex-1 break-words bm-body-s text-bm-ink">{m.title}</span>
          <span className="shrink-0 bm-label-m text-bm-muted">{m.top}</span>
        </button>
      </li>
    ))}
  </ul>
);

// The card's shell while the season's peer group is being built (spec D4
// c), in the archetype's place instead of the profile's older radar: the
// lede says the ranks are coming ("Five archetype metrics. Ranks appear
// once the Test League 16U peer group is ready."), and the metrics show as
// plain values, a tile each (the section's stat tiles; two a row on
// phones), with no (i) and no radar. `shell` is
// athletePerformanceModel.archetypeShellView(); a shell with no metrics yet
// (a group being built for another season) is its lede alone.
const ShellTiles = ({ shell, phone }) => (
  <div className={phone ? "grid grid-cols-2 gap-[10px]" : "grid grid-cols-2 gap-[10px] sm:grid-cols-3 lg:grid-cols-5"}>
    {shell.metrics.map((m) => (
      <PerfTile key={m.key} value={m.raw} label={m.code} note={m.title} />
    ))}
  </div>
);

// `view` (the card) or, while the peer group is being built, `shell` (its
// shell). One card on screen for both: when the section's own re-ask
// brings the ranks, the shell fills in where it stands (the section, its
// heading and its toggle stay the same elements), so a keyboard or screen
// reader user keeps their place.
const PlayerArchetypeCard = ({ view = null, shell = null, approximate = false, cardProps = {} }) => {
  const phone = usePhoneTheme();
  const [info, setInfo] = useState(false);
  // One panel for the radar's points and the metric rows; new metrics
  // close it.
  const tap = useTapPanel(tapScopeOf(view?.axes));
  if (!view && !shell) return null;

  let body = null;
  if (!view) {
    if (shell.metrics.length > 0) body = <ShellTiles shell={shell} phone={phone} />;
  } else {
    const openIndex = openIndexIn(tap, view.metrics.length);
    const detail = (i) => (view.metrics[i] ? <MetricDetail metric={view.metrics[i]} /> : null);
    const radar = (
      <RadarPlot axes={view.axes} ariaLabel={view.radarLabel} renderDetail={detail} detailLabel="Archetype metrics" tap={phone ? tap : undefined} />
    );
    const identity = (
      <div className="flex flex-col items-center gap-2 text-center">
        <span className="inline-flex min-h-[30px] max-w-full items-center gap-2 rounded-full border border-bm-strong bg-white px-3 py-1 bm-label-l text-bm-ink">
          <ArchetypeDot color={view.color} size={10} />
          <span className="break-words">{view.label}</span>
        </span>
        {view.description && <p className="max-w-[420px] bm-body-m text-bm-muted">{view.description}</p>}
      </div>
    );
    const read = <InsightList insights={view.insights} variant="read" label="Archetype read" />;
    body = phone ? (
      <>
        {radar}
        <MetricRows metrics={view.metrics} tap={tap} />
        <TapPanel id={tap.panelId} open={openIndex !== null} onClose={tap.close}>
          {openIndex !== null ? detail(openIndex) : null}
        </TapPanel>
        {identity}
        {view.impact && <ImpactStat impact={view.impact} approximate={approximate} />}
        {read}
      </>
    ) : (
      <div className="grid gap-6 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:items-start">
        <div className="flex min-w-0 flex-col items-center gap-4">
          {radar}
          {identity}
          {view.impact && <ImpactStat impact={view.impact} approximate={approximate} />}
        </div>
        <div className="min-w-0">{read}</div>
      </div>
    );
  }

  return (
    <>
      <CollapsibleCard
        title="Player Archetype"
        subtitle={view ? view.lede : shell.lede}
        info={view ? { label: "What the archetype metrics mean", onOpen: () => setInfo(true) } : undefined}
        {...cardProps}
      >
        {body}
      </CollapsibleCard>
      {view && info && <PlayerArchetypeModal view={view} onClose={() => setInfo(false)} />}
    </>
  );
};

export default PlayerArchetypeCard;
